import { z } from 'zod';
export const ticketStatusKeys = [
    'new',
    'in_progress',
    'waiting',
    'resolved',
    'closed',
];
/** Segments of the status switch (Backoffice 05–08) and the board columns (Backoffice 03). */
export const ticketStatusLabels = {
    new: 'Neu',
    in_progress: 'In Arbeit',
    waiting: 'Wartet auf Kunde',
    resolved: 'Gelöst',
    closed: 'Geschlossen',
};
/** Board columns with cards; „Geschlossen" is the dashed tile „38 in den letzten 30 Tagen" (Backoffice 03). */
export const ticketBoardColumns = ['new', 'in_progress', 'waiting', 'resolved'];
export const ticketBoardFilters = ['all', 'today', 'exceeded', 'unassigned'];
export const ticketBoardFilterLabels = {
    all: 'Alle',
    today: 'Frist heute',
    exceeded: 'Überschritten',
    unassigned: 'Unzugeordnet',
};
export const ticketPillLabels = {
    new: 'neu',
    in_progress: 'in Arbeit',
    waiting: 'wartet auf Kunde',
    resolved: 'gelöst',
    closed: 'geschlossen',
    exceeded: 'Frist überschritten',
    reopened: 'wiedereröffnet',
};
export const ticketPillTones = {
    new: 'accent',
    in_progress: 'info',
    waiting: 'neutral',
    resolved: 'success',
    closed: 'neutral',
    exceeded: 'danger',
    reopened: 'warning',
};
export const ticketSources = ['mailbox', 'portal', 'internal', 'chatbot'];
/** Channel of the board card's contact line („Familie Özdemir · Kundenportal", Backoffice 03). */
export const ticketSourceChannelLabels = {
    portal: 'Kundenportal',
    chatbot: 'Kundenportal',
    mailbox: 'E-Mail',
    internal: 'Telefon',
};
/** „Eingang über Kundenportal · heute, 11:48 · Familie Özdemir" (Backoffice 05–08). */
export const ticketSourceSublineLabels = {
    portal: 'Eingang über Kundenportal',
    chatbot: 'Eingang über Kundenportal',
    mailbox: 'Eingang per E-Mail',
    internal: 'Eingang intern',
};
/** Channel of a thread entry („heute 11:48 · Kundenportal"). */
export const ticketMessageChannelLabels = {
    email: 'E-Mail',
    portal: 'Kundenportal',
    internal: 'Telefon',
};
export const ticketUrgencies = ['normal', 'urgent'];
/** Internal label „#2041" (Backoffice 03–08). */
export function formatTicketNumber(number) {
    return `#${number}`;
}
/** Customer label „ST-2041" (Mandantenportal 26/28). */
export function formatPortalTicketNumber(number) {
    return `ST-${number}`;
}
/** „AS-0031" (After-Sales 04). */
export function formatAfterSalesNumber(number) {
    return `AS-${String(number).padStart(4, '0')}`;
}
export const portalTicketPillLabels = {
    in_progress: 'In Bearbeitung',
    waiting: 'Wartet auf Ihre Antwort',
    resolved: 'Gelöst',
    closed: 'Abgeschlossen',
};
export const portalTicketPillTones = {
    in_progress: 'warning',
    waiting: 'info',
    resolved: 'success',
    closed: 'neutral',
};
// --- after-sales -------------------------------------------------------------------------------------
export const afterSalesStatusKeys = [
    'reported',
    'with_contractor',
    'scheduled',
    'done',
];
/** Board columns (After-Sales 03) and the record pill (After-Sales 04). */
export const afterSalesStatusLabels = {
    reported: 'Gemeldet',
    with_contractor: 'Beim Handwerker',
    scheduled: 'Termin',
    done: 'Erledigt',
};
export const afterSalesStatusTones = {
    reported: 'info',
    with_contractor: 'warning',
    scheduled: 'accent',
    done: 'success',
};
/** Customer status sentence after an external assignment (After-Sales 04, H9). */
export const AFTER_SALES_EXTERNAL_STATUS_TEXT = 'In Bearbeitung beim Bauträger. Wir melden uns mit einem Termin.';
// --- request schemas ------------------------------------------------------------------------------------
const uuid = z.string().uuid();
const dayKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Bitte ein Datum angeben.');
const title = z.string().trim().min(1, 'Bitte einen Betreff angeben.').max(200);
const bodyText = z.string().trim().min(1, 'Bitte einen Text eingeben.').max(20_000);
/** POST /tickets — „Ticket anlegen" (phone request, channel `internal`). */
export const createTicketBodySchema = z.object({
    contactId: uuid,
    title,
    text: bodyText,
    categoryId: uuid.nullable().optional(),
    urgency: z.enum(ticketUrgencies).optional(),
    dealId: uuid.nullable().optional(),
    unitId: uuid.nullable().optional(),
});
/** POST /tickets/:id/status — the status switch of Backoffice 05–08. */
export const ticketStatusBodySchema = z.object({
    to: z.enum(ticketStatusKeys),
    reason: z.string().trim().max(2000).nullable().optional(),
});
export const ticketAssignBodySchema = z.object({ assigneeId: uuid });
export const patchTicketBodySchema = z
    .object({
    categoryId: uuid.nullable().optional(),
    title: title.optional(),
    urgency: z.enum(ticketUrgencies).optional(),
    dealId: uuid.nullable().optional(),
    unitId: uuid.nullable().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Keine Änderung angegeben.' });
/** Text fields of POST /tickets/:id/messages (multipart, ≤ 5 files). */
export const ticketMessageFieldsSchema = z.object({
    kind: z.enum(['reply', 'note']),
    text: bodyText,
    statusAfter: z.enum(['in_progress', 'waiting', 'resolved']).optional(),
});
/** Text field of POST /portal/tickets/:id/messages (multipart, ≤ 3 files). */
export const portalTicketMessageFieldsSchema = z.object({ text: bodyText });
// --- support mailbox --------------------------------------------------------------------------------------
export const supportMailboxFilters = ['unassigned', 'today', 'all'];
export const supportMailboxFilterLabels = {
    unassigned: 'Unzugeordnet',
    today: 'Heute zugeordnet',
    all: 'Alle',
};
/** POST /support-mailbox/:id/assign — exactly one target (contact, deal or after-sales case). */
export const supportMailboxAssignBodySchema = z
    .object({ contactId: uuid.optional(), dealId: uuid.optional(), caseId: uuid.optional() })
    .refine((value) => [value.contactId, value.dealId, value.caseId].filter((v) => v !== undefined).length === 1, { message: 'Bitte genau ein Ziel wählen.' });
/** POST /support-mailbox/:id/ticket. */
export const supportMailboxTicketBodySchema = z.object({
    contactId: uuid.nullable().optional(),
    categoryId: uuid.nullable().optional(),
    title: title.optional(),
});
// --- after-sales ------------------------------------------------------------------------------------------
/** Text fields of POST /after-sales (multipart, ≤ 5 photos) — „Vorgang anlegen". */
export const createAfterSalesFieldsSchema = z.object({
    unitId: uuid,
    title,
    description: z.string().trim().max(20_000).optional(),
    contactId: uuid.optional(),
    ticketId: uuid.optional(),
    reportedChannel: z.enum(['portal', 'email', 'phone', 'internal']).optional(),
});
/** POST /after-sales/:id/assign-external — a developer of the organization or a free company name. */
export const assignExternalBodySchema = z
    .object({
    developerId: uuid.optional(),
    company: z.string().trim().min(1).max(200).optional(),
    purpose: z.string().trim().max(200).optional(),
    contactPerson: z.string().trim().max(200).optional(),
    phone: z.string().trim().max(60).optional(),
    deadlineOn: dayKey.optional(),
})
    .refine((value) => value.developerId !== undefined || value.company !== undefined, {
    message: 'Bitte einen Bauträger oder eine Firma angeben.',
    path: ['company'],
});
export const scheduleAfterSalesBodySchema = z.object({
    appointmentAt: z.string().datetime({ offset: true }),
});
export const extendAfterSalesBodySchema = z.object({ deadlineOn: dayKey });
export const afterSalesLogBodySchema = z.object({ text: z.string().trim().min(1).max(2000) });
export const patchAfterSalesBodySchema = z
    .object({
    customerStatusText: z.string().trim().max(500).nullable().optional(),
    internalAssigneeId: uuid.nullable().optional(),
    title: title.optional(),
    description: z.string().trim().max(20_000).nullable().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Keine Änderung angegeben.' });
// --- after-sales handover (Deal-Pipeline 22) ------------------------------------------------------------------
/** POST /deals/invoices/:id/complete — „Abschluss bestätigen". `nextStep` follows the stage dialog's schema. */
export const completeInvoiceBodySchema = z.object({
    receivedAt: dayKey,
    handoverToId: uuid,
});
//# sourceMappingURL=tickets.js.map